import { CardCarousel, CardCarouselArrows } from "@ui/components/CardCarousel";
import { Icon } from "@ui/components/Icon";
import { useCardCarousel } from "@ui/primitives/Carousel";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

export interface HomepageSectionProps {
  children: ReactNode;
  subtitle: string;
  title: string;
  viewAllHref?: string;
}

export function HomepageSection({ children, subtitle, title, viewAllHref }: HomepageSectionProps) {
  const carousel = useCardCarousel();

  const heading = (
    <>
      <div className="flex items-center justify-between gap-4 md:justify-start">
        <h2 className="shrink-0 text-2xl font-extrabold leading-8 tracking-[-0.24px] text-neutral-900 lg:text-display-sm lg:leading-[44px] lg:tracking-[-0.36px]">
          {title}
        </h2>
        {viewAllHref && (
          <div
            aria-hidden="true"
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-50"
          >
            <Icon name="arrowRight" size="20" />
          </div>
        )}
      </div>
      <p className="text-base font-normal leading-5 text-neutral-600 md:text-lg md:leading-6 lg:text-2xl lg:leading-8 lg:tracking-[-0.24px]">
        {subtitle}
      </p>
    </>
  );

  return (
    <div className="flex w-full flex-col gap-3 pb-2 md:gap-5 lg:gap-6">
      <div className="flex items-start gap-2.5">
        <div className="flex min-w-0 flex-1">
          {viewAllHref ? (
            <Link className="flex flex-1 flex-col no-underline md:flex-initial" href={viewAllHref}>
              {heading}
            </Link>
          ) : (
            <div className="flex flex-col">{heading}</div>
          )}
        </div>
        <CardCarouselArrows carousel={carousel} className="hidden shrink-0 py-2 md:flex" />
      </div>
      <CardCarousel ref={carousel.ref}>{children}</CardCarousel>
    </div>
  );
}
